import StickyTableResponsive from '../../common/StickyTableResponsive';
import SortableTh from '../../common/SortableTh';
import React, { useCallback, useEffect, useState } from 'react';
import { useTableSort } from '../../../hooks/useTableSort';
import {
  AdminDeactivateModal,
  AdminDeleteModal,
  AdminPeriodFields,
  AdminValidityActions,
  AdminValidityBadge,
  defaultAdminPeriod,
} from './KkpAdminShared';
import AdminFormLabel from '../../admin/AdminFormLabel';
import { ADMIN_FIELD_HELP } from '../../../utils/adminHelpContent';
import kkpService from '../../../services/kkpService';
import { notify } from '../../common/AppToast';
import { AppButton, AppInput, AppModal, AppSpinner } from '../../ui';

const STATUT_SORT = {
  statut: { type: 'string', getValue: (row) => row.value },
  couleur: { type: 'string', getValue: (row) => row.couleur },
  periode: { type: 'string', getValue: (row) => row.validity_label },
  etat: { type: 'string', getValue: (row) => row.validity_status },
};

const emptyStatutForm = () => ({
  value: '',
  couleur: '#FFFF00',
  valid_from_year: new Date().getFullYear(),
  valid_from_month: new Date().getMonth() + 1,
});

const KkpAdminStatutsSection = () => {
  const [data, setData] = useState({ statuts: [], mois: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statutForm, setStatutForm] = useState(emptyStatutForm);
  const [modalOpen, setModalOpen] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivatePeriod, setDeactivatePeriod] = useState(defaultAdminPeriod);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const moisOptions = data.mois ?? [];
  const { sortedRows: sortedStatuts, sort: sortStatuts, toggleSort: toggleStatuts, getSortIcon: getStatutsIcon } = useTableSort(data.statuts ?? [], STATUT_SORT);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await kkpService.listPlanningAdmin();
      setData({ statuts: result.statuts ?? [], mois: result.mois ?? [] });
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const wrapSave = async (fn, successMessage) => {
    setSaving(true);
    try {
      await fn();
      if (successMessage) notify.success(successMessage);
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openCreateModal = () => {
    setStatutForm(emptyStatutForm());
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setStatutForm(emptyStatutForm());
  };

  const submitCreate = () => wrapSave(
    () => kkpService.createPlanningStatut(statutForm).then(() => closeModal()),
    'Statut ajouté',
  );

  const handleColorChange = async (item, couleur) => {
    if (saving) return;
    const next = couleur || '#FFFF00';
    if ((item.couleur || '#FFFF00') === next) return;

    setSaving(true);
    try {
      await kkpService.updatePlanningOption(item.id, { couleur: next });
      setData((prev) => ({
        ...prev,
        statuts: (prev.statuts ?? []).map((row) => (
          row.id === item.id ? { ...row, couleur: next } : row
        )),
      }));
      notify.success('Couleur mise à jour');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setSaving(true);
    try {
      await kkpService.deactivatePlanningOption(deactivateTarget.id, deactivatePeriod);
      setDeactivateTarget(null);
      notify.success('Option désactivée');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReactivate = async (id) => {
    setSaving(true);
    try {
      await kkpService.reactivatePlanningOption(id);
      notify.success('Option réactivée');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await kkpService.deletePlanningOption(deleteTarget.id);
      setDeleteTarget(null);
      notify.success('Élément supprimé');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AppSpinner />;

  return (
    <>
      <div className="card admin-data-card">
        <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
          <span>Statuts de phase ({sortedStatuts.length})</span>
          <AppButton
            size="sm"
            variant="danger-600"
            icon="mdi:plus"
            disabled={saving}
            onClick={openCreateModal}
          >
            Ajouter
          </AppButton>
        </div>
        <StickyTableResponsive>
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <SortableTh sortKey="statut" label="Statut" sort={sortStatuts} onSort={toggleStatuts} getSortIcon={getStatutsIcon} />
                <SortableTh sortKey="couleur" label="Couleur" sort={sortStatuts} onSort={toggleStatuts} getSortIcon={getStatutsIcon} />
                <SortableTh sortKey="periode" label="Période" sort={sortStatuts} onSort={toggleStatuts} getSortIcon={getStatutsIcon} />
                <SortableTh sortKey="etat" label="État" sort={sortStatuts} onSort={toggleStatuts} getSortIcon={getStatutsIcon} />
                <SortableTh label="Actions" className="text-end" sortable={false} />
              </tr>
            </thead>
            <tbody>
              {sortedStatuts.map((item) => (
                <tr key={item.id} className={item.validity_status === 'expired' ? 'opacity-50' : ''}>
                  <td className="fw-medium">{item.value}</td>
                  <td>
                    <AppInput
                      type="color"
                      className="form-control-color"
                      value={item.couleur || '#FFFF00'}
                      disabled={saving}
                      title="Modifier la couleur"
                      onChange={(e) => handleColorChange(item, e.target.value)}
                    />
                  </td>
                  <td className="text-sm">{item.validity_label}</td>
                  <td><AdminValidityBadge status={item.validity_status} /></td>
                  <td className="text-end">
                    <AdminValidityActions
                      item={item}
                      saving={saving}
                      onDeactivate={(t) => { setDeactivateTarget(t); setDeactivatePeriod(defaultAdminPeriod()); }}
                      onReactivate={handleReactivate}
                      onDelete={(t) => setDeleteTarget(t)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </StickyTableResponsive>
      </div>

      <AppModal
        show={modalOpen}
        title="Ajouter un statut de phase"
        onClose={closeModal}
        size="lg"
        centered
        contentAs="form"
        contentProps={{
          onSubmit: (e) => {
            e.preventDefault();
            submitCreate();
          },
        }}
        footer={(
          <>
            <AppButton type="button" variant="outline-secondary" onClick={closeModal} disabled={saving}>
              Annuler
            </AppButton>
            <AppButton type="submit" variant="danger-600" loading={saving}>
              Ajouter
            </AppButton>
          </>
        )}
      >
        <p className="text-secondary-light small mb-3">
          Couleur et libellé visibles dans les plannings MAG, SET et Newsletters
        </p>
        <div className="row g-3">
          <div className="col-md-6">
            <AdminFormLabel help={ADMIN_FIELD_HELP.statutLabel.help} helpExample={ADMIN_FIELD_HELP.statutLabel.helpExample} required>
              Libellé
            </AdminFormLabel>
            <AppInput required value={statutForm.value} onChange={(e) => setStatutForm((f) => ({ ...f, value: e.target.value }))} />
          </div>
          <div className="col-md-3">
            <AdminFormLabel help={ADMIN_FIELD_HELP.statutCouleur.help}>Couleur</AdminFormLabel>
            <AppInput
              type="color"
              className="form-control-color w-100"
              value={statutForm.couleur || '#FFFF00'}
              onChange={(e) => setStatutForm((f) => ({ ...f, couleur: e.target.value }))}
            />
          </div>
          <AdminPeriodFields
            year={statutForm.valid_from_year}
            month={statutForm.valid_from_month}
            moisOptions={moisOptions}
            onYearChange={(y) => setStatutForm((f) => ({ ...f, valid_from_year: y }))}
            onMonthChange={(m) => setStatutForm((f) => ({ ...f, valid_from_month: m }))}
          />
        </div>
      </AppModal>

      <AdminDeactivateModal
        target={deactivateTarget}
        period={deactivatePeriod}
        moisOptions={moisOptions}
        saving={saving}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={handleDeactivate}
        onPeriodChange={setDeactivatePeriod}
      />
      <AdminDeleteModal
        target={deleteTarget}
        contextKey="statut"
        saving={saving}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default KkpAdminStatutsSection;
